import type { ClobMarketConfig } from '@cpc/tx';
import { clobBaseType } from '@cpc/tx';

import { CPC_SYMBOL, DGC_TYPE_ARG } from '~/utils/cpcCoin';

export type CpcAssetKind = 'native' | 'coin' | 'package' | 'shared';

export interface CpcAssetEntry {
	key: string;
	label: string;
	symbol?: string;
	description?: string;
	kind: CpcAssetKind;
	/** Coin type tag (native / DATA) */
	typeTag?: string;
	/** Shared object or package id for getObject */
	objectId?: string;
}

function viteEnv(name: string): string | undefined {
	const v = import.meta.env[name];
	return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function isDgcType(typeTag?: string): boolean {
	return !!typeTag && typeTag.includes('::dgc::DGC');
}

/** 当前交易对对应的 CLOB 合约 / 币种；无 market 时回退 Vite 默认 DGC/DATA。 */
export function getCpcAssetEntries(market?: ClobMarketConfig | null): CpcAssetEntry[] {
	if (market?.packageId && market.registryId && market.orderBookId) {
		return entriesFromMarket(market);
	}
	return entriesFromVite();
}

function entriesFromMarket(market: ClobMarketConfig): CpcAssetEntry[] {
	const baseSymbol = market.baseSymbol ?? (isDgcType(clobBaseType(market)) ? 'DGC' : 'BASE');
	const quoteSymbol = market.quoteSymbol ?? 'DATA';
	const pair = `${baseSymbol}/${quoteSymbol}`;
	const baseType = clobBaseType(market);
	const marketKey = market.marketId ?? market.orderBookId;

	const entries: CpcAssetEntry[] = [
		{
			key: `gas-dgc-${marketKey}`,
			label: CPC_SYMBOL,
			symbol: CPC_SYMBOL,
			kind: 'native',
			typeTag: DGC_TYPE_ARG,
			description: 'Native gas coin (framework)',
		},
	];

	if (!isDgcType(baseType)) {
		entries.push({
			key: `base-coin-${marketKey}`,
			label: baseSymbol,
			symbol: baseSymbol,
			kind: 'coin',
			typeTag: baseType,
			description: `${pair} 标的`,
		});
	}

	if (market.dataTypeTag) {
		entries.push({
			key: `quote-coin-${marketKey}`,
			label: quoteSymbol,
			symbol: quoteSymbol,
			kind: 'coin',
			typeTag: market.dataTypeTag,
			description: `${pair} 报价币`,
		});
	}

	entries.push(
		{
			key: `clob-package-${marketKey}`,
			label: `${baseSymbol}-CLOB`,
			kind: 'package',
			objectId: market.packageId,
			description: `${pair} CLOB 包`,
		},
		{
			key: `clob-registry-${marketKey}`,
			label: 'CLOB Registry',
			kind: 'shared',
			objectId: market.registryId,
			description: `${pair} 市场注册表`,
		},
		{
			key: `clob-order-book-${marketKey}`,
			label: `Order book (${quoteSymbol}/${baseSymbol})`,
			kind: 'shared',
			objectId: market.orderBookId,
			description: `${pair} 订单簿`,
		},
	);

	return entries;
}

/** Homepage / 无 Hub 时：Vite 默认 DGC/DATA */
function entriesFromVite(): CpcAssetEntry[] {
	const clobPackage = viteEnv('VITE_CPC_CLOB_PACKAGE_ID');
	const registry = viteEnv('VITE_CPC_REGISTRY_ID');
	const orderBook = viteEnv('VITE_CPC_ORDER_BOOK_ID');
	const dataCoinType =
		viteEnv('VITE_CPC_DATA_COIN_TYPE') ??
		(clobPackage ? `${clobPackage}::data::DATA` : undefined);

	const entries: CpcAssetEntry[] = [
		{
			key: 'cpc-native',
			label: CPC_SYMBOL,
			symbol: CPC_SYMBOL,
			kind: 'native',
			typeTag: DGC_TYPE_ARG,
			description: 'Native gas coin (framework)',
		},
	];

	if (dataCoinType) {
		entries.push({
			key: 'data-coin',
			label: 'DATA',
			symbol: 'DATA',
			kind: 'coin',
			typeTag: dataCoinType,
			description: 'CLOB DATA coin',
		});
	}

	if (clobPackage) {
		entries.push({
			key: 'clob-package',
			label: 'DGC-CLOB',
			kind: 'package',
			objectId: clobPackage,
			description: 'Published CLOB Move package',
		});
	}

	if (registry) {
		entries.push({
			key: 'clob-registry',
			label: 'CLOB Registry',
			kind: 'shared',
			objectId: registry,
			description: 'Shared registry (pairs)',
		});
	}

	if (orderBook) {
		entries.push({
			key: 'clob-order-book',
			label: 'Order book (DATA/DGC)',
			kind: 'shared',
			objectId: orderBook,
			description: 'Shared order book',
		});
	}

	return entries;
}
